import { createFileRoute } from "@tanstack/react-router";
import { useSearch } from "@tanstack/react-router";
import type { ClientBuilderUserPermissions } from "api-types/builderUsers/builderUsersResponses";
import type { ClientBuilderRole, ClientCedarPolicy } from "api-types/permissions/permissionsResponses";
import type { ClientUserHydratedWithRoles } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import DocumentationTab from "lib/components/users/DocumentationTab";
import ResourcesTab from "lib/components/users/ResourcesTab";
import RoleDetailView from "lib/components/users/RoleDetailView";
import RoleFormModal from "lib/components/users/RoleFormModal";
import RolesTab from "lib/components/users/RolesTab";
import UserDetailView from "lib/components/users/UserDetailView";
import UsersTab from "lib/components/users/UsersTab";
import { getBuilderPoliciesURL, getBuilderRolesURL } from "lib/endpointHelpers";
import { fetcher, useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { getDefaultPermissions } from "lib/security/permissions";
import { defaultBuilderRoles, type DefaultRoleName } from "lib/security/shared";
import { deleteWithTimeout, postWithTimeout, putWithTimeout } from "lib/utils/fetchAuthHelpers";
import { useEffect, useRef, useState } from "react";
import Button from "ui/buttons/Button";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import useAppContext from "ui/hooks/useAppContext";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import IconPlus from "ui/icons/IconPlus";
import withAuthentication from "../../../../components/withAuthentication";

type UserManagementTab = "users" | "roles" | "resources" | "documentation";

const TAB_NAMES: UserManagementTab[] = ["users", "roles", "resources", "documentation"];

const TAB_PRETTY_NAMES: Record<UserManagementTab, string> = {
  users: "Users",
  roles: "Roles",
  resources: "Resources",
  documentation: "Documentation",
};

function UsersPage() {
  const navigate = useAppNavigate();
  const searchParams = useSearch({ strict: false }) as Record<string, string>;
  const { builderId } = useAppRouterParams();
  const { setUserNotification } = useAppContext();
  const { tab, menuItems } = useTabbedMenu<UserManagementTab>(TAB_NAMES, {
    prettyNames: TAB_PRETTY_NAMES,
  });
  const [selectedRole, setSelectedRole] = useState<ClientBuilderRole | null>(null);
  const [selectedUser, setSelectedUser] = useState<ClientUserHydratedWithRoles | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editRole, setEditRole] = useState<ClientBuilderRole | null>(null);
  const [duplicateRole, setDuplicateRole] = useState<ClientBuilderRole | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<ClientBuilderRole | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: roles, mutate: mutateRoles } = useFetch<ClientBuilderRole[]>(
    getBuilderRolesURL({ builderId: builderId ?? "" }),
    [builderId],
  );

  // Fetch users to compute user counts per role
  const { data: users } = useFetch<ClientUserHydratedWithRoles[]>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/users-internal`,
    [builderId],
  );

  // Compute user counts per role
  const userCountsByRole: Record<string, number> = {};
  if (users && builderId) {
    for (const user of users) {
      const builderAccount = user.builderAccounts.find((ba) => ba.builderId === builderId);
      if (builderAccount?.roles) {
        for (const role of builderAccount.roles) {
          userCountsByRole[role.id] = (userCountsByRole[role.id] ?? 0) + 1;
        }
      }
    }
  }

  // Get roleId and userId from query params
  const roleIdFromQuery = typeof searchParams.roleId === "string" ? searchParams.roleId : undefined;
  const userIdFromQuery = typeof searchParams.userId === "string" ? searchParams.userId : undefined;

  // Track if we've already initialized from URL (to avoid re-setting on every render)
  const [initializedRoleFromUrl, setInitializedRoleFromUrl] = useState(false);
  const [initializedUserFromUrl, setInitializedUserFromUrl] = useState(false);

  // When roles are loaded and roleId is in the URL, select that role (only on initial load)
  useEffect(() => {
    if (roles && roleIdFromQuery && !initializedRoleFromUrl) {
      const role = roles.find((r) => r.id === roleIdFromQuery);
      if (role) {
        setSelectedRole(role);
      }
      setInitializedRoleFromUrl(true);
    }
  }, [roles, roleIdFromQuery, initializedRoleFromUrl]);

  // When users are loaded and userId is in the URL, select that user (only on initial load)
  useEffect(() => {
    if (users && userIdFromQuery && !initializedUserFromUrl) {
      const user = users.find((u) => {
        const builderAccount = u.builderAccounts.find((ba) => ba.builderId === builderId);
        return builderAccount?.id === userIdFromQuery;
      });
      if (user) {
        setSelectedUser(user);
      }
      setInitializedUserFromUrl(true);
    }
  }, [users, userIdFromQuery, builderId, initializedUserFromUrl]);

  // Track previous tab to detect tab changes
  const prevTabRef = useRef(tab);

  // Clear selection and query params when switching tabs
  useEffect(() => {
    if (prevTabRef.current === tab) {
      return;
    }
    const prevTab = prevTabRef.current;
    prevTabRef.current = tab;

    // Clear state for the tab we're leaving
    if (prevTab === "users") {
      setSelectedUser(null);
    }
    if (prevTab === "roles") {
      setSelectedRole(null);
    }

    // Clear query params when switching tabs
    const { pathname } = window.location;
    const params = new URLSearchParams(window.location.search);
    params.delete("roleId");
    params.delete("userId");

    const queryString = params.toString();
    const newUrl = `${pathname}${queryString ? `?${queryString}` : ""}#${tab}`;
    window.history.replaceState(null, "", newUrl);
  }, [tab]);

  // Update URL when selecting/deselecting a role (preserving the hash for tab routing)
  const handleSelectRole = (role: ClientBuilderRole | null) => {
    setSelectedRole(role);

    // Build the new URL preserving the hash
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;
    const [pathWithQuery, hash] = currentUrl.split("#");
    const [pathname] = pathWithQuery.split("?");
    const params = new URLSearchParams(window.location.search);

    // Remove roleId, then re-add if selecting
    params.delete("roleId");
    if (role) {
      params.set("roleId", role.id);
    }

    const queryString = params.toString();
    const newUrl = `${pathname}${queryString ? `?${queryString}` : ""}${hash ? `#${hash}` : ""}`;
    void navigate({ to: newUrl, replace: true });
  };

  // Update URL when selecting/deselecting a user (preserving the hash for tab routing)
  const handleSelectUser = (user: ClientUserHydratedWithRoles | null) => {
    setSelectedUser(user);

    // Build the new URL preserving the hash
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;
    const [pathWithQuery, hash] = currentUrl.split("#");
    const [pathname] = pathWithQuery.split("?");
    const params = new URLSearchParams(window.location.search);

    // Remove userId, then re-add if selecting
    params.delete("userId");
    if (user) {
      const builderAccount = user.builderAccounts.find((ba) => ba.builderId === builderId);
      if (builderAccount) {
        params.set("userId", builderAccount.id);
      }
    }

    const queryString = params.toString();
    const newUrl = `${pathname}${queryString ? `?${queryString}` : ""}${hash ? `#${hash}` : ""}`;
    void navigate({ to: newUrl, replace: true });
  };

  const handleCreateRole = async (data: { name: string; description?: string }) => {
    const response = await postWithTimeout(getBuilderRolesURL({ builderId: builderId ?? "" }), data);
    if (response.ok) {
      setUserNotification({
        message: `Role "${data.name}" created successfully`,
        messageType: "success",
      });
      void mutateRoles();
    } else {
      setUserNotification({
        message: "Failed to create role",
        messageType: "error",
      });
    }
  };

  const handleEditRole = async (data: { name: string; description?: string }) => {
    if (!editRole) {
      return;
    }
    const response = await putWithTimeout(`${getBuilderRolesURL({ builderId: builderId ?? "" })}/${editRole.id}`, data);
    if (response.ok) {
      setUserNotification({
        message: `Role "${data.name}" updated successfully`,
        messageType: "success",
      });
      void mutateRoles();
    } else {
      setUserNotification({
        message: "Failed to update role",
        messageType: "error",
      });
    }
  };

  const handleCopyRole = async (data: { name: string; description?: string }) => {
    if (!duplicateRole) {
      return;
    }

    // Step 1: Create the new role
    const createResponse = await postWithTimeout(getBuilderRolesURL({ builderId: builderId ?? "" }), data);
    if (!createResponse.ok) {
      setUserNotification({
        message: "Failed to create role",
        messageType: "error",
      });
      return;
    }

    const newRole = (await createResponse.json()) as ClientBuilderRole;

    // Check if this is a default role (which uses hardcoded permissions, not database policies)
    const isDefault = defaultBuilderRoles.some((r) => r.name === duplicateRole.name);

    try {
      if (isDefault) {
        // For default roles, get permissions from hardcoded defaults and create policies
        const defaultPermissions = getDefaultPermissions(duplicateRole.name as DefaultRoleName);

        // Separate permissions into ALLOW and DENY groups
        const allowedActions: string[] = [];
        const deniedActions: string[] = [];

        for (const [action, value] of Object.entries(defaultPermissions) as [
          keyof ClientBuilderUserPermissions,
          "ALLOW" | "DENY" | "DEFER",
        ][]) {
          if (value === "ALLOW") {
            allowedActions.push(action);
          } else if (value === "DENY") {
            deniedActions.push(action);
          }
          // DEFER permissions don't need a policy
        }

        // Create PERMIT policy for allowed actions
        if (allowedActions.length > 0) {
          await postWithTimeout(getBuilderPoliciesURL({ builderId: builderId ?? "" }), {
            name: `${data.name} Permissions`,
            effect: "PERMIT",
            actions: allowedActions,
            roleId: newRole.id,
          });
        }

        // Create FORBID policy for denied actions
        if (deniedActions.length > 0) {
          await postWithTimeout(getBuilderPoliciesURL({ builderId: builderId ?? "" }), {
            name: `${data.name} Denied Permissions`,
            effect: "FORBID",
            actions: deniedActions,
            roleId: newRole.id,
          });
        }
      } else {
        // For custom roles, fetch and copy existing database policies
        const policies = await fetcher<ClientCedarPolicy[]>(getBuilderPoliciesURL({ builderId: builderId ?? "" }));
        const sourcePolicies = policies.filter((p) => p.scope === "ROLE" && p.roleId === duplicateRole.id);

        for (const policy of sourcePolicies) {
          await postWithTimeout(getBuilderPoliciesURL({ builderId: builderId ?? "" }), {
            name: `${data.name} ${policy.effect === "PERMIT" ? "Permissions" : "Denied Permissions"}`,
            effect: policy.effect,
            actions: policy.actions,
            roleId: newRole.id,
          });
        }
      }

      setUserNotification({
        message: `Role "${data.name}" created successfully with copied permissions`,
        messageType: "success",
      });
    } catch {
      // Role was created but policies failed to copy
      setUserNotification({
        message: `Role "${data.name}" created but failed to copy permissions`,
        messageType: "warning",
      });
    }

    void mutateRoles();
  };

  const handleDeleteRoleClick = (role: ClientBuilderRole) => {
    setRoleToDelete(role);
  };

  const handleConfirmDeleteRole = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!roleToDelete) {
      return;
    }
    setIsDeleting(true);
    const response = await deleteWithTimeout(
      `${getBuilderRolesURL({ builderId: builderId ?? "" })}/${roleToDelete.id}`,
    );
    setIsDeleting(false);
    if (response.ok) {
      setUserNotification({
        message: `Role "${roleToDelete.name}" deleted successfully`,
        messageType: "success",
      });
      void mutateRoles();
      setRoleToDelete(null);
    } else {
      setUserNotification({
        message: "Failed to delete role",
        messageType: "error",
      });
    }
  };

  return (
    <BuilderPortalLayout header="User Management" pageTitle="User Management" selected="users" menuItems={menuItems}>
      <div className="builder-portal-layout-content">
        {tab === "users" && (
          <>
            {selectedUser ? (
              <UserDetailView user={selectedUser} builderId={builderId ?? ""} onBack={() => handleSelectUser(null)} />
            ) : (
              <UsersTab onViewUser={(user) => handleSelectUser(user)} />
            )}
          </>
        )}
        {tab === "roles" && (
          <>
            {selectedRole ? (
              <RoleDetailView role={selectedRole} builderId={builderId ?? ""} onBack={() => handleSelectRole(null)} />
            ) : (
              <>
                <SectionHeader
                  title="Roles"
                  subtitle="Manage roles and permissions for users"
                  headerButton={
                    <Button btnStyle="primary" btnSize="large" onClick={() => setIsCreateModalOpen(true)}>
                      <IconPlus size={14} />
                      Create Role
                    </Button>
                  }
                  hr
                />
                <RolesTab
                  roles={roles}
                  userCounts={userCountsByRole}
                  onViewRole={(role) => handleSelectRole(role)}
                  onEditRole={(role) => setEditRole(role)}
                  onDuplicateRole={(role) => setDuplicateRole(role)}
                  onDeleteRole={handleDeleteRoleClick}
                />
              </>
            )}
          </>
        )}
        {tab === "documentation" && <DocumentationTab roles={roles} />}
        {tab === "resources" && <ResourcesTab builderId={builderId ?? ""} />}
      </div>

      {/* Create Role Modal */}
      <RoleFormModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSubmit={handleCreateRole} />

      {/* Edit Role Modal */}
      <RoleFormModal
        open={!!editRole}
        onClose={() => setEditRole(null)}
        onSubmit={handleEditRole}
        editRole={editRole ?? undefined}
      />

      {/* Duplicate Role Modal */}
      <RoleFormModal
        open={!!duplicateRole}
        onClose={() => setDuplicateRole(null)}
        onSubmit={handleCopyRole}
        duplicateFrom={duplicateRole ?? undefined}
      />

      {/* Delete Role Modal */}
      {roleToDelete && (
        <ConfirmDeleteModal
          onClose={() => setRoleToDelete(null)}
          onConfirm={handleConfirmDeleteRole}
          entityType="Role"
          entityName={roleToDelete.name}
          message={`Are you sure you want to delete the role "${roleToDelete.name}"? This action cannot be undone.`}
          isDeleting={isDeleting}
          requireEntityNameEntry={false}
        />
      )}
    </BuilderPortalLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/users/")({
  component: withAuthentication(UsersPage, { permissions: ["VIEW_STAFF_PERMISSIONS"] }),
});
